<template>
	<div class="login-view">
		<!-- 自动登录加载状态 -->
		<div v-if="autoLoginLoading" class="auto-login-loading">
			<div class="loading-content">
				<div class="loading-spinner">
					<i class="el-icon-loading"></i>
				</div>
				<div class="loading-text">正在自动登录...</div>
				<div class="loading-phone">手机号：{{ autoLoginPhone }}</div>
			</div>
		</div>

		<!-- 微信 OAuth 授权加载状态 -->
		<div v-else-if="wechatOAuthLoading" class="auto-login-loading">
			<div class="loading-content">
				<div class="loading-spinner">
					<i class="el-icon-loading"></i>
				</div>
				<div class="loading-text">正在微信授权登录...</div>
			</div>
		</div>

		<!-- 正常登录界面 -->
		<div v-else class="login-content">
			<div class="login-form">
				<div class="login-brand">
					<img class="logo" src="../../public/logo-128.png" />
					<div>傻蛋AI</div>
				</div>

				<!-- Tab标签页 -->
				<div class="custom-tabs" v-if="isInVSCodeEnvironment()">
					<!-- 自定义Tab头部 -->
					<div class="custom-tabs-header">
						<div
							class="custom-tab-item"
							:class="{ active: activeTab === 'qrcode' }"
							@click="forceSetActiveTab('qrcode')">
							扫码登录
						</div>
						<div
							class="custom-tab-item"
							:class="{ active: activeTab === 'sms' }"
							@click="forceSetActiveTab('sms')">
							短信登录
						</div>
						<div
							class="custom-tab-item"
							:class="{ active: activeTab === 'account' }"
							@click="forceSetActiveTab('account')">
							账号登录
						</div>
					</div>
					<!-- Tab内容 -->
					<div class="custom-tabs-content">
						<!-- 账号登录 -->
						<div v-show="activeTab === 'account'" class="custom-tab-pane">
							<el-form :model="accountForm" status-icon :rules="accountRules" ref="accountForm"
								@keyup.enter.native="submitAccountForm">
								<el-form-item prop="userName">
									<el-input v-model="accountForm.userName" autocomplete="off" placeholder="请输入用户名"
										prefix-icon="el-icon-user">
									</el-input>
								</el-form-item>
								<el-form-item prop="password">
									<el-input type="password" v-model="accountForm.password" autocomplete="off"
										placeholder="请输入密码" prefix-icon="el-icon-lock">
									</el-input>
								</el-form-item>
								<el-form-item>
									<el-button type="primary" @click="submitAccountForm" :loading="accountLoginLoading" class="login-btn">登录</el-button>
								</el-form-item>
							</el-form>
						</div>

					<!-- 短信登录 -->
					<div v-show="activeTab === 'sms'" class="custom-tab-pane">
						<el-form :model="smsForm" status-icon :rules="smsRules" ref="smsForm"
							@keyup.enter.native="submitSmsForm">
							<el-form-item prop="userName">
								<el-input v-model="smsForm.userName" autocomplete="off" placeholder="请输入手机号"
									prefix-icon="el-icon-phone">
								</el-input>
							</el-form-item>
							<el-form-item prop="password">
								<div class="verification-code-row">
									<el-input v-model="smsForm.password" autocomplete="off" placeholder="请输入验证码"
										prefix-icon="el-icon-message" class="code-input">
									</el-input>
									<el-button type="primary" @click="sendCode" :disabled="countdown > 0"
										class="send-code-btn">
										{{ countdown > 0 ? `${countdown}s后重发` : '发送验证码' }}
									</el-button>
								</div>
							</el-form-item>
							<el-form-item>
								<el-button type="primary" @click="phoneLogin" :loading="smsLoginLoading" class="login-btn">登录</el-button>
							</el-form-item>
						</el-form>
					</div>

					<!-- 扫码登录 -->
					<div v-show="activeTab === 'qrcode'" class="custom-tab-pane">
						<!-- 二维码登录界面 -->
						<div v-if="!bindPhone" class="qrcode-container">
							<div class="qrcode-box">
								<div v-if="qrcodeStatus === 'loading'" class="qrcode-loading">
									<i class="el-icon-loading"></i>
									<p>二维码生成中...</p>
								</div>
								<div v-else-if="qrcodeStatus === 'active'" class="qrcode-active">
									<div class="qrcode-image">
										<!-- 这里可以放置实际的二维码图片 -->
										<div class="mock-qrcode" v-if="!wxTicket">
											<i class="el-icon-mobile-phone"></i>
											<p>二维码加载中...</p>
										</div>
										<div class="qrcode-real" v-else>
											<img :src="`https://mp.weixin.qq.com/cgi-bin/showqrcode?ticket=${wxTicket}`"
												alt="微信登录二维码" />
										</div>
									</div>
									<p class="qrcode-tip">请使用微信扫描二维码登录</p>
								</div>
								<div v-else-if="qrcodeStatus === 'expired'" class="qrcode-expired">
									<i class="el-icon-refresh"></i>
									<p>二维码已过期</p>
									<p class="auto-refresh-tip">正在自动刷新...</p>
									<el-button type="text" @click="refreshQrcode" class="manual-refresh-btn">
										<i class="el-icon-refresh-right"></i>
										立即刷新
									</el-button>
								</div>
								<div v-else-if="qrcodeStatus === 'scanned'" class="qrcode-scanned">
									<i class="el-icon-success"></i>
									<p>扫描成功，请在手机上确认登录</p>
								</div>
							</div>
						</div>

						<!-- 手机号绑定界面 -->
						<div v-else class="bind-phone-container">
							<div class="bind-phone-header">
								<p v-if="isNewUser">首次登录，请绑定手机号完成注册</p>
								<p v-else>请绑定手机号以完成登录</p>
							</div>
							<el-form :model="bindPhoneForm" status-icon :rules="bindPhoneRules" ref="bindPhoneForm"
								@keyup.enter.native="submitBindPhone">
								<el-form-item prop="phone">
									<el-input v-model="bindPhoneForm.phone" autocomplete="off" placeholder="请输入手机号"
										prefix-icon="el-icon-phone">
									</el-input>
								</el-form-item>
								<el-form-item prop="code">
									<div class="verification-code-row">
										<el-input v-model="bindPhoneForm.code" autocomplete="off" placeholder="请输入验证码"
											prefix-icon="el-icon-message" class="code-input">
										</el-input>
										<el-button type="primary" @click="sendBindPhoneCode"
											:disabled="bindPhoneCountdown > 0" class="send-code-btn">
											{{ bindPhoneCountdown > 0 ? `${bindPhoneCountdown}s后重发` : '发送验证码' }}
										</el-button>
									</div>
								</el-form-item>
								<el-form-item>
									<el-button type="primary" @click="submitBindPhone"
										:loading="bindPhoneLoading" class="login-btn">确认绑定</el-button>
								</el-form-item>
								<el-form-item>
									<el-button type="text" @click="cancelBindPhone" class="cancel-btn">取消绑定</el-button>
								</el-form-item>
							</el-form>
						</div>
					</div>
				</div>
			</div>

			<!-- Web端使用原生el-tabs -->
			<el-tabs v-else v-model="activeTab" class="login-tabs">
				<!-- 扫码登录 -->
				<el-tab-pane label="扫码登录" name="qrcode">
					<!-- 二维码登录界面 -->
					<div v-if="!bindPhone" class="qrcode-container">
						<div class="qrcode-box">
							<div v-if="qrcodeStatus === 'loading'" class="qrcode-loading">
								<i class="el-icon-loading"></i>
								<p>二维码生成中...</p>
							</div>
							<div v-else-if="qrcodeStatus === 'active'" class="qrcode-active">
								<div class="qrcode-image">
									<!-- 这里可以放置实际的二维码图片 -->
									<div class="mock-qrcode" v-if="!wxTicket">
										<i class="el-icon-mobile-phone"></i>
										<p>二维码加载中...</p>
									</div>
									<div class="qrcode-real" v-else>
										<img :src="`https://mp.weixin.qq.com/cgi-bin/showqrcode?ticket=${wxTicket}`"
											alt="微信登录二维码" />
									</div>
								</div>
								<p class="qrcode-tip">请使用微信扫描二维码登录</p>
							</div>
							<div v-else-if="qrcodeStatus === 'expired'" class="qrcode-expired">
								<i class="el-icon-refresh"></i>
								<p>二维码已过期</p>
								<p class="auto-refresh-tip">正在自动刷新...</p>
								<el-button type="text" @click="refreshQrcode" class="manual-refresh-btn">
									<i class="el-icon-refresh-right"></i>
									立即刷新
								</el-button>
							</div>
							<div v-else-if="qrcodeStatus === 'scanned'" class="qrcode-scanned">
								<i class="el-icon-success"></i>
								<p>扫描成功，请在手机上确认登录</p>
							</div>
						</div>
					</div>

					<!-- 手机号绑定界面 -->
					<div v-else class="bind-phone-container">
						<div class="bind-phone-header">
							<p v-if="isNewUser">首次登录，请绑定手机号完成注册</p>
							<p v-else>请绑定手机号以完成登录</p>
						</div>
						<el-form :model="bindPhoneForm" status-icon :rules="bindPhoneRules" ref="bindPhoneFormWeb"
							@keyup.enter.native="submitBindPhone">
							<el-form-item prop="phone">
								<el-input v-model="bindPhoneForm.phone" autocomplete="off" placeholder="请输入手机号"
									prefix-icon="el-icon-phone">
								</el-input>
							</el-form-item>
							<el-form-item prop="code">
								<div class="verification-code-row">
									<el-input v-model="bindPhoneForm.code" autocomplete="off" placeholder="请输入验证码"
										prefix-icon="el-icon-message" class="code-input">
									</el-input>
									<el-button type="primary" @click="sendBindPhoneCode"
										:disabled="bindPhoneCountdown > 0" class="send-code-btn">
										{{ bindPhoneCountdown > 0 ? `${bindPhoneCountdown}s后重发` : '发送验证码' }}
									</el-button>
								</div>
							</el-form-item>
							<el-form-item>
								<el-button type="primary" @click="submitBindPhone"
									:loading="bindPhoneLoading" class="login-btn">确认绑定</el-button>
							</el-form-item>
							<el-form-item>
								<el-button type="text" @click="cancelBindPhone" class="cancel-btn">取消绑定</el-button>
							</el-form-item>
						</el-form>
					</div>
				</el-tab-pane>

				<!-- 短信登录 -->
				<el-tab-pane label="短信登录" name="sms">
					<el-form :model="smsForm" status-icon :rules="smsRules" ref="smsFormWeb"
						@keyup.enter.native="submitSmsForm">
						<el-form-item prop="userName">
							<el-input v-model="smsForm.userName" autocomplete="off" placeholder="请输入手机号"
								prefix-icon="el-icon-phone">
							</el-input>
						</el-form-item>
						<el-form-item prop="password">
							<div class="verification-code-row">
								<el-input v-model="smsForm.password" autocomplete="off" placeholder="请输入验证码"
									prefix-icon="el-icon-message" class="code-input">
								</el-input>
								<el-button type="primary" @click="sendCode" :disabled="countdown > 0"
									class="send-code-btn">
									{{ countdown > 0 ? `${countdown}s后重发` : '发送验证码' }}
								</el-button>
							</div>
						</el-form-item>
						<el-form-item>
							<el-button type="primary" @click="phoneLogin" :loading="smsLoginLoading" class="login-btn">登录</el-button>
						</el-form-item>
					</el-form>
				</el-tab-pane>

				<!-- 账号登录 -->
				<el-tab-pane label="账号登录" name="account">
					<el-form :model="accountForm" status-icon :rules="accountRules" ref="accountFormWeb"
						@keyup.enter.native="submitAccountForm">
						<el-form-item prop="userName">
							<el-input v-model="accountForm.userName" autocomplete="off" placeholder="请输入用户名"
								prefix-icon="el-icon-user">
							</el-input>
						</el-form-item>
						<el-form-item prop="password">
							<el-input type="password" v-model="accountForm.password" autocomplete="off"
								placeholder="请输入密码" prefix-icon="el-icon-lock">
							</el-input>
						</el-form-item>
						<el-form-item>
							<el-button type="primary" @click="submitAccountForm" :loading="accountLoginLoading" class="login-btn">登录</el-button>
						</el-form-item>
					</el-form>
				</el-tab-pane>
			</el-tabs>

				<div class="register">
					<a v-if="window.vscodeServices" href="#" @click.prevent="openRegisterInBrowser">没有账号,前往注册</a>
					<router-link v-else to="/register">没有账号,前往注册</router-link>
				</div>
			</div>
		</div>
		<icp></icp>
	</div>
</template>

<script>
import Icp from '../components/common/Icp.vue'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

export default {
	name: "login",
	components: {
		Icp
	},
	props: {
		isVSCodeEnvironment: {
			type: Boolean,
			default: false
		},
		vscodeServices: {
			type: Object,
			default: null
		}
	},
	data() {
		var checkUsername = (rule, value, callback) => {
			if (!value) {
				return callback(new Error('请输入用户名'));
			}
			callback();
		};
		var checkPassword = (rule, value, callback) => {
			if (value === '') {
				callback(new Error('请输入密码'));
			}
			callback();
		};
		var checkPhone = (rule, value, callback) => {
			if (!value) {
				return callback(new Error('请输入手机号'));
			}
			const phoneRegex = /^1[3-9]\d{9}$/;
			if (!phoneRegex.test(value)) {
				return callback(new Error('请输入正确的手机号格式'));
			}
			callback();
		};
		var checkSmsCode = (rule, value, callback) => {
			if (!value) {
				return callback(new Error('请输入验证码'));
			}
			callback();
		};

		return {
			// 自动登录相关状态
			autoLoginLoading: false,
			autoLoginPhone: '',
			window: window, // 添加window对象到data中，以便在模板中使用

			// 微信公众号 OAuth 相关状态
			wechatOAuthLoading: false,    // OAuth 加载状态
			isWeChatOAuthFlow: false,     // 是否为 OAuth 流程
			wechatOAuthState: null,       // OAuth 恢复的 state（包含 redirect 等信息）

			// 登录按钮loading状态
			accountLoginLoading: false, // 账号登录loading
			smsLoginLoading: false, // 短信登录loading
			bindPhoneLoading: false, // 绑定手机号loading

			activeTab: 'qrcode', // 默认显示扫码登录
			tabKey: 0, // 用于强制刷新tabs的key
			countdown: 0, // 倒计时秒数
			timer: null, // 定时器
			qrcodeStatus: 'active', // 二维码状态: loading, active, expired, scanned
			qrcodeTimer: null, // 二维码定时器
			wxTicketTimer: null, // 微信ticket定时器
			checkLoginTimer: null, // CheckLogin定时器
			UUID: uuidv4(),
			wxTicket: '',
			bindPhone: false,
			bindPhoneCountdown: 0, // 绑定手机号验证码倒计时
			bindPhoneTimer: null, // 绑定手机号验证码定时器
			currentOpenid: '', // 当前扫码用户的openid
			userInfo: null,
			// 二维码相关状态
			wxTicketReady: false, // ticket 是否已获取成功
			wxTicketRetryCount: 0, // ticket 获取重试次数
			maxWxTicketRetry: 3, // 最大重试次数
			isNewUser: false, // 是否为新用户（im-platform 中不存在）
			// 账号登录表单
			accountForm: {
				// 重要：区分terminal和terminalNo
				// terminal: 登录终端类型（动态），根据实际环境设置
				// terminalNo: 账号隔离标识（静态），必须为0（默认账号类型）
				terminal: 0, // 默认值，在mounted中会根据环境重新设置
				terminalNo: 0, // 必须为0，这是默认账号类型
				userName: '',
				password: ''
			},
			accountRules: {
				userName: [{
					validator: checkUsername,
					trigger: 'blur'
				}],
				password: [{
					validator: checkPassword,
					trigger: 'blur'
				}]
			},

			// 短信登录表单
			smsForm: {
				// 重要：区分terminal和terminalNo
				// terminal: 登录终端类型（动态），根据实际环境设置
				// terminalNo: 账号隔离标识（静态），必须为0（默认账号类型）
				terminal: 0, // 默认值，在mounted中会根据环境重新设置
				terminalNo: 0, // 必须为0，这是默认账号类型
				userName: '',
				password: ''
			},
			smsRules: {
				userName: [{
					validator: checkPhone,
					trigger: 'blur'
				}],
				password: [{
					validator: checkSmsCode,
					trigger: 'blur'
				}]
			},

			// 绑定手机号表单
			bindPhoneForm: {
				phone: '',
				code: ''
			},
			bindPhoneRules: {
				phone: [{
					validator: checkPhone,
					trigger: 'blur'
				}],
				code: [{
					validator: checkSmsCode,
					trigger: 'blur'
				}]
			}
		};
	},
	computed: {
		// 根据环境动态获取终端类型
		currentTerminalType() {
			// 使用与ChatBox.vue相同的终端检测逻辑
			return this.getCurrentTerminal();
		}
	},
	methods: {
		// 强制设置activeTab - VSCode环境专用
		forceSetActiveTab(tabName) {
			console.log('[Login] Force setting tab to:', tabName, 'from:', this.activeTab);

			// 直接设置activeTab
			this.activeTab = tabName;

			// 强制刷新DOM
			this.$forceUpdate();

			// 使用nextTick确保DOM更新
			this.$nextTick(() => {
				console.log('[Login] Tab switched to:', this.activeTab);

				// 触发watch中的逻辑
				if (tabName === 'qrcode') {
					if (this.qrcodeStatus === 'active') {
						this.startQrcodeTimer();
					}
					// 启动微信ticket定时器（CheckLogin定时器由 GetWxTicket 成功后启动）
					this.startWxTicketTimer();
				} else {
					this.stopWxTicketTimer();
					this.stopCheckLoginTimer();
					this.wxTicketReady = false;
				}
			});
		},
		
		// 处理Tab点击事件 - 解决VSCode环境下的切换问题
		handleTabClick(tab) {
			console.log('[Login] Tab clicked:', tab.name, 'current activeTab:', this.activeTab);
			
			// 在VSCode环境下强制更新activeTab
			if (this.isInVSCodeEnvironment()) {
				// 使用nextTick确保DOM更新
				this.$nextTick(() => {
					// 强制更新activeTab的值
					this.activeTab = tab.name;
					
					// 如果还是没有切换成功，强制刷新组件
					if (this.activeTab !== tab.name) {
						console.log('[Login] Force switching to tab:', tab.name);
						this.activeTab = tab.name;
						this.tabKey++; // 更新key强制重新渲染
					}
				});
			}
		},
		
		// 检测是否在VSCode环境中
		isInVSCodeEnvironment() {
			return (
				this.isVSCodeEnvironment || // props传入
				window.BoxIMComponents || // BoxIM组件存在
				window.vscodeServices || // vscode服务存在
				window.parent !== window || // 在iframe中
				window.acquireVsCodeApi || // VSCode WebView API
				navigator.userAgent.includes('VSCode') || // User Agent
				window.location.protocol === 'vscode-webview:' // VSCode WebView协议
			);
		},

		// 检测是否在微信浏览器中
		isInWeChatBrowser() {
			const ua = navigator.userAgent.toLowerCase();
			return ua.includes('micromessenger');
		},

		// 发起微信公众号 OAuth 授权
		initiateWeChatOAuth() {
			console.log('[WeChatOAuth] 发起微信公众号授权');

			// 保存当前状态到 sessionStorage（用于授权后恢复）
			const state = btoa(JSON.stringify({
				redirect: this.$route.query.redirect || '',
				agentId: this.$route.query.agentId || '',
				timestamp: Date.now()
			}));
			sessionStorage.setItem('wechat_oauth_state', state);

			// 微信公众号 AppID - TODO: 替换为实际的 AppID
			const appId = 'wx8a564d4558779f25';

			// 构造回调 URL
			const redirectUri = encodeURIComponent(window.location.origin + '/#/login');

			// 使用 snsapi_base 静默授权，只获取 openid
			const scope = 'snsapi_base';

			// 构造微信授权 URL
			const oauthUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`;

			console.log('[WeChatOAuth] 跳转到微信授权页面:', oauthUrl);
			window.location.href = oauthUrl;
		},

		// 处理微信 OAuth 回调
		async handleWeChatOAuthCallback() {
			const code = this.getUrlParam('code');
			if (!code) {
				return false;  // 非 OAuth 回调
			}

			this.wechatOAuthLoading = true;
			this.isWeChatOAuthFlow = true;

			// 🔥 从 sessionStorage 恢复保存的 OAuth state（包含 redirect 等信息）
			let savedState = null;
			try {
				const stateStr = sessionStorage.getItem('wechat_oauth_state');
				if (stateStr) {
					savedState = JSON.parse(atob(stateStr));
					console.log('[WeChatOAuth] 恢复保存的 state:', savedState);
				}
			} catch (e) {
				console.warn('[WeChatOAuth] 解析保存的 state 失败:', e);
			}

			try {
				// 用 code 换取 openid（使用新接口，不注册用户）
				const response = await axios({
					url: `https://shadan.web.service.thinkgs.cn/jeecg-boot/sys/getWxOpenid`,
					method: 'get',
					params: { code },
					timeout: 10000
				});

				// 返回格式: { success: true, result: { openid: 'xxx', unionid: 'xxx' } }
				let openid = null;
				if (response.data) {
					if (response.data.result && response.data.result.openid) {
						// 标准返回格式
						openid = response.data.result.openid;
					} else if (response.data.openid) {
						// 直接返回格式
						openid = response.data.openid;
					} else if (response.data.success === false) {
						// 明确失败
						throw new Error(response.data.message || '获取openid失败');
					}
				}

				if (openid) {
					this.currentOpenid = openid;
					console.log('[WeChatOAuth] 获取到 openid:', this.currentOpenid);

					// 🔥 保存恢复的 state 到组件状态，供登录成功后使用
					this.wechatOAuthState = savedState;

					// 复用现有的 openid 登录流程（由 im-platform 处理用户注册）
					await this.processOpenidLogin(openid);
				} else {
					throw new Error(response.data?.errmsg || response.data?.message || '获取openid失败');
				}
			} catch (error) {
				console.error('[WeChatOAuth] 微信授权失败:', error);

				this.$message.error('微信授权失败，请重试');
				this.wechatOAuthLoading = false;
				// 清除标记，允许重新授权
				sessionStorage.removeItem('wechat_oauth_attempted');
			}

			// 清理 URL 中的 code 参数
			this.cleanupOAuthParams();
			return true;
		},

		// 统一的 openid 登录处理（供扫码登录和 OAuth 登录共用）
		async processOpenidLogin(openid) {
			console.log('[OpenidLogin] 开始处理 openid 登录:', openid);

			try {
				const data = await this.$http({
					url: "/findUserByOpenid",
					method: 'post',
					data: {
						openid: openid,
						terminalNo: 0  // 必须为0，默认账号类型
					}
				});

				console.log('[OpenidLogin] findUserByOpenid 响应:', data);
				this.userInfo = data;

				if (data != null && data.phone != null && data.phone != '') {
					// 用户已存在且绑定手机号 -> 直接登录
					console.log('[OpenidLogin] 用户已绑定手机号，直接登录');
					this.accountForm.userName = data.userName;
					this.accountForm.password = data.password;
					this.accountForm.phone = data.phone;
					this.accountForm.tokenKey = data.tokenKey;
					this.submitLogin2(this.accountForm);
				} else if (data != null && data.id != null) {
					// 用户存在但未绑定手机号
					console.log('[OpenidLogin] 用户存在但未绑定手机号，进入绑定流程');
					this.isNewUser = false;
					this.bindPhone = true;
					this.wechatOAuthLoading = false;
				} else {
					// 用户不存在（新用户）
					console.log('[OpenidLogin] 用户不存在（新用户），进入注册绑定流程');
					this.isNewUser = true;
					this.bindPhone = true;
					this.wechatOAuthLoading = false;
				}
			} catch (error) {
				console.error('[OpenidLogin] OpenID登录失败:', error);
				this.wechatOAuthLoading = false;
				throw error;
			}
		},

		// 清理 URL 中的 OAuth 参数
		cleanupOAuthParams() {
			// 移除 URL 中的 code 和 state 参数，保持 URL 整洁
			const hash = window.location.hash;
			const hashPath = hash.split('?')[0];
			window.history.replaceState(null, '', window.location.pathname + hashPath);
			console.log('[WeChatOAuth] 已清理 URL 参数');
		},
		
		// 获取当前终端类型
		getCurrentTerminal() {
			// 根据环境判断当前终端类型
			if (typeof window !== 'undefined') {
				// 检测VSCode环境 - 多种方式检测
				if (window.parent !== window || // 在iframe中
					window.acquireVsCodeApi || // VSCode WebView API
					window.SharedServicesAccessor || // 我们的VSCode服务
					navigator.userAgent.includes('VSCode') || // User Agent
					window.location.protocol === 'vscode-webview:') { // VSCode WebView协议
					return this.$enums.TERMINAL_TYPE.VSCODE; // VSCode
				}
				
				// 检测Electron环境
				if (window.require || 
					window.process?.type || 
					window.isElectron ||
					navigator.userAgent.includes('Electron')) {
					return this.$enums.TERMINAL_TYPE.PC; // PC客户端
				}
				
				// 检测移动端App环境
				if (window.cordova || 
					window.PhoneGap || 
					window.phonegap ||
					navigator.userAgent.includes('wv') || // WebView
					navigator.userAgent.includes('Mobile')) {
					return this.$enums.TERMINAL_TYPE.APP; // App
				}
				
				return this.$enums.TERMINAL_TYPE.WEB; // Web
			}
			return this.$enums.TERMINAL_TYPE.WEB; // 默认Web
		},

		// 获取URL参数 - 支持哈希路由参数
		getUrlParam(name) {
			console.log('当前完整URL:', window.location.href);
			console.log('当前search:', window.location.search);
			console.log('当前hash:', window.location.hash);

			// 首先尝试从普通查询参数获取
			const urlParams = new URLSearchParams(window.location.search);
			let value = urlParams.get(name);
			console.log(`从普通查询参数获取 ${name}:`, value);

			// 如果普通查询参数中没有找到，则从hash中查找
			if (!value && window.location.hash) {
				const hash = window.location.hash;
				console.log('处理hash:', hash);

				// 查找hash中的查询参数部分
				const hashQueryIndex = hash.indexOf('?');
				if (hashQueryIndex !== -1) {
					const hashQuery = hash.substring(hashQueryIndex + 1);
					console.log('hash查询参数字符串:', hashQuery);
					const hashParams = new URLSearchParams(hashQuery);
					value = hashParams.get(name);
					console.log(`从hash查询参数获取 ${name}:`, value);
				} else {
					console.log('hash中没有找到查询参数');
				}
			}

			console.log(`最终获取参数 ${name}:`, value);
			return value;
		},

		// 自动登录处理
		async handleAutoLogin() {
			const phone = this.getUrlParam('phone');

			if (phone) {
				// 验证手机号格式
				const phoneRegex = /^1[3-9]\d{9}$/;
				if (!phoneRegex.test(phone)) {
					this.$message.error('URL中的手机号格式不正确');
					return;
				}

				this.autoLoginPhone = phone;
				this.autoLoginLoading = true;

				try {
					console.log('开始自动登录，手机号:', phone);

					// 调用后端接口获取用户信息
					const userInfo = await this.$http({
						url: "/phoneLoginInfo",
						method: 'post',
						data: { 
							phone: phone, 
							// 注意：terminalNo必须为0（默认账号类型）
							terminalNo: 0 
						},
						timeout: 10000
					});

					console.log("自动登录用户信息查询成功:", userInfo);

					if (userInfo && userInfo.userName && userInfo.password) {
						// 构造登录表单数据
						const loginForm = {
							// 重要：区分terminal和terminalNo
							// terminal: 登录终端类型（4=插件端）
							// terminalNo: 账号隔离标识，必须为0（默认账号类型）
							terminal: this.$enums.TERMINAL_TYPE.PLUGIN,  // 插件端
							terminalNo: 0, // 必须为0，默认账号类型
							userName: userInfo.userName,
							password: userInfo.password,
							phone: userInfo.phone
						};

						// 执行登录
						await this.submitAutoLogin(loginForm);
					} else {
						throw new Error('用户信息不完整或用户不存在');
					}

				} catch (error) {
					console.error("自动登录失败:", error);
					this.autoLoginLoading = false;

					// 显示错误信息
					const errorMessage = error.message || '自动登录失败，请手动登录';
					this.$message.error(errorMessage);

					// 3秒后隐藏加载状态，显示正常登录界面
					setTimeout(() => {
						this.autoLoginLoading = false;
					}, 3000);
				}
			}
		},

		// 自动登录提交
		async submitAutoLogin(formData) {
			try {
				const data = await this.$http({
					url: "/loginByPhone",
					method: 'post',
					data: formData
				});

				console.log("自动登录成功:", data);

				// 保存用户信息到cookie
				this.setCookie('username', formData.userName);
				// 保存token
				sessionStorage.setItem("accessToken", data.accessToken);
				sessionStorage.setItem("refreshToken", data.refreshToken);

			// 🔥 智能体分享场景 - 保存基础用户信息到 localStorage
			this.saveUserInfoToLocalStorage({
				id: data.userId || data.id,
				userId: data.userId || data.id,
				username: formData.userName,
				accessToken: data.accessToken,
				refreshToken: data.refreshToken,
				tokenKey: data.tokenKey,
				avatar: data.avatar,
				email: data.email
			});

				this.$message.success("自动登录成功");

				// 检查是否在VSCode环境中
				console.log('[AutoLogin] Checking VSCode environment:', {
					isVSCodeEnvironment: this.isVSCodeEnvironment,
					hasVscodeServices: !!this.vscodeServices,
					vscodeServicesType: typeof this.vscodeServices
				});

				if (this.isVSCodeEnvironment && this.vscodeServices) {
					// 通知VSCode登录成功
					const userInfo = {
						userId: data.userId || data.id,
						username: formData.userName,
						avatar: data.avatar,
						email: data.email,
						status: 'online',
						lastLoginTime: Date.now()
					};
					console.log('[AutoLogin] Notifying VSCode of login success:', userInfo);

					// 🔥 触发success事件给UniversalIM组件
					this.$emit('success', userInfo);

					// 尝试多种方式调用登录
					let loginSuccess = false;

					try {
						// 方式1：通过$vscode.get获取IUserStateService
						const userStateService = this.$vscode?.get?.('IUserStateService');
						if (userStateService?.login) {
							console.log("[坑爹位置]:", userInfo);
							await userStateService.login(userInfo, data.accessToken);
							console.log('[AutoLogin] 方式1：UserStateService.login调用完成，应该触发面板切换');
							loginSuccess = true;
						}
					} catch (error) {
						console.warn('[AutoLogin] 方式1失败:', error);
					}

					// 方式2：直接调用vscodeServices.login
					if (!loginSuccess && this.vscodeServices?.login) {
						try {
							await this.vscodeServices.login(userInfo, data.accessToken);
							console.log('[AutoLogin] 方式2：vscodeServices.login调用完成');
							loginSuccess = true;
						} catch (error) {
							console.warn('[AutoLogin] 方式2失败:', error);
						}
					}

					// 方式3：通过window.vscodeServices调用
					if (!loginSuccess && window.vscodeServices?.login) {
						try {
							await window.vscodeServices.login(userInfo, data.accessToken);
							console.log('[AutoLogin] 方式3：window.vscodeServices.login调用完成');
							loginSuccess = true;
						} catch (error) {
							console.warn('[AutoLogin] 方式3失败:', error);
						}
					}

					if (loginSuccess) {
						// 在VSCode环境中也需要更新store中的用户信息
						this.$http({
							url: '/user/self',
							method: 'GET'
						}).then((fullUserInfo) => {
							fullUserInfo.userId = fullUserInfo.id + "";
							fullUserInfo.username = fullUserInfo.nickName;
							fullUserInfo.accessToken = data.accessToken;
							fullUserInfo.apiKey = data.tokenKey;
							fullUserInfo.terminal = 2;
							
							// 保存用户信息到 store - 修复切换用户时 ChatBox 中 mine.id 不更新的问题
							this.$store.commit('setUserInfo', fullUserInfo);
							this.$store.commit('setIsLoggedIn', true);
						});
						
						this.vscodeServices.showInfo('自动登录成功！');
						console.log('[AutoLogin] VSCode notification sent successfully');
					} else {
						console.error('[AutoLogin] 所有登录方式都失败');
						this.vscodeServices.showError('登录通知失败');
					}
				} else {
					console.log('[AutoLogin] Not in VSCode environment or services not available, using router');
					// Web环境使用Vue Router - 使用统一的跳转处理
					// 注意：自动登录时phone参数用于跳转到plugin页面
					const phoneParam = this.getUrlParam('phone');
					if (phoneParam) {
						console.log('[AutoLogin] URL contains phone parameter, redirecting to /plugin');
						this.$router.push("/plugin");
					} else {
						// 使用统一的跳转处理
						this.handleLoginSuccess();
					}
				}

			} catch (error) {
				console.error("自动登录提交失败:", error);
				throw error;
			}
		},

		// 验证手机号格式
		validatePhoneNumber(phone) {
			const phoneRegex = /^1[3-9]\d{9}$/;
			return phoneRegex.test(phone);
		},

		// 发送验证码
		sendCode() {
			// 验证手机号是否输入
			if (!this.smsForm.userName) {
				this.$message.error('请先输入手机号');
				return;
			}

			// 验证手机号格式
			const phoneRegex = /^1[3-9]\d{9}$/;
			if (!phoneRegex.test(this.smsForm.userName)) {
				this.$message.error('请输入正确的手机号格式');
				return;
			}

			// 调用后端发送验证码API
			console.log('发送验证码到手机号:', this.smsForm.userName);

			// 使用原生axios调用外部接口，绕过项目的响应拦截器
			axios({
				url: "https://shadan.web.service.thinkgs.cn/jeecg-boot/sys/sendsms",
				method: 'post',
				data: { mobile: this.smsForm.userName },
				timeout: 10000
			}).then(response => {
				console.log('验证码发送响应:', response.data);

				// 适配外部接口的返回格式：success字段表示成功
				if (response.data && response.data.success === true) {
					this.$message.success('验证码已发送');
					// 注意：验证码不再存储在前端，由后端 /verifySms 接口验证
					this.startCountdown();
				} else {
					console.error('服务器返回错误:', response.data);
					this.$message.error(response.data.message || '验证码发送失败');
				}
			}).catch(error => {
				console.error('验证码发送失败:', error);

				// 处理不同类型的错误
				let errorMessage = '验证码发送失败，请稍后重试';
				if (error.response && error.response.data) {
					// 如果是HTTP错误响应
					const data = error.response.data;
					if (data.success === false) {
						errorMessage = data.message || errorMessage;
					}
				} else if (error.message) {
					errorMessage = error.message;
				}

				this.$message.error(errorMessage);
			});
		},
		async GetWxTicket() {
			console.log('[GetWxTicket] 调用GetWxTicket方法，UUID:', this.UUID);
			try {
				const response = await axios({
					url: `https://shadan.web.service.thinkgs.cn/jeecg-boot/sys/getWxTicket?senceId=${this.UUID}`,
					method: 'get',
					timeout: 10000
				});
				console.log("[GetWxTicket] 获取微信ticket成功:", response.data);

				if (response.data && response.data.result) {
					this.wxTicket = response.data.result;
					this.wxTicketReady = true;
					this.wxTicketRetryCount = 0;

					// ticket 获取成功后，才启动扫码轮询
					console.log('[GetWxTicket] ticket获取成功，启动CheckLogin轮询');
					this.startCheckLoginTimer();
				} else {
					throw new Error('ticket为空');
				}
			} catch (error) {
				console.error('[GetWxTicket] 获取微信ticket失败:', error);
				this.wxTicketReady = false;
				this.wxTicketRetryCount++;

				// 重试机制：最多重试3次
				if (this.wxTicketRetryCount < this.maxWxTicketRetry) {
					console.log(`[GetWxTicket] 第${this.wxTicketRetryCount}次重试...`);
					setTimeout(() => {
						this.GetWxTicket();
					}, 2000); // 2秒后重试
				} else {
					console.error('[GetWxTicket] 已达到最大重试次数，停止重试');
					this.$message.error('获取二维码失败，请刷新页面重试');
				}
			}
		},
		// 开始倒计时
		startCountdown() {
			this.countdown = 60;
			this.timer = setInterval(() => {
				this.countdown--;
				if (this.countdown <= 0) {
					clearInterval(this.timer);
					this.timer = null;
				}
			}, 1000);
		},
		CheckLogin() {
			axios({
				url: `https://shadan.web.service.thinkgs.cn/jeecg-boot/sys/signInBySenceId?senceId=${this.UUID}`,
				method: 'get',
				timeout: 10000
			}).then(response => {
				console.log("获取微信扫码登录:", response.data);
				if (response.data.success) {
					this.stopCheckLoginTimer();
					console.log("获取微信扫码登录 openid:", response.data.result.userInfo.openid);
					this.currentOpenid = response.data.result.userInfo.openid; // 保存openid

					// 复用统一的 openid 登录处理
					this.processOpenidLogin(this.currentOpenid);
				}
			}).catch(error => {
				console.error('获取微信扫码登录失败:', error);
			});
		},
		// 账号登录提交
		submitAccountForm() {
			// 根据环境选择正确的ref
			const formRef = this.isInVSCodeEnvironment() ? this.$refs.accountForm : (this.$refs.accountFormWeb || this.$refs.accountForm);
			if (formRef) {
				formRef.validate(async (valid) => {
					if (valid) {
						this.submitLogin(this.accountForm);
					}
				});
			}
		},

		// 短信登录提交
		submitSmsForm() {
			// 根据环境选择正确的ref
			const formRef = this.isInVSCodeEnvironment() ? this.$refs.smsForm : (this.$refs.smsFormWeb || this.$refs.smsForm);
			if (formRef) {
				formRef.validate(async (valid) => {
					if (valid) {
						this.submitLogin(this.smsForm);
					}
				});
			}
		},

		// 辅助方法：判断是否来自智能体分享页
		isFromAgentProfile() {
			const redirect = this.$route.query.redirect;
			if (!redirect) {
				return false;
			}
			// 检查 redirect 参数是否包含智能体分享/详情页路径
			return redirect.includes('/agent/profile/') || redirect.includes('/agent/share/');
		},

		// 辅助方法：保存用户信息到 localStorage
		saveUserInfoToLocalStorage(userInfo) {
			try {
				const savedUserInfo = {
					id: userInfo.id || userInfo.userId,
					userId: userInfo.id || userInfo.userId,
					username: userInfo.username || userInfo.nickName,
					nickName: userInfo.nickName || userInfo.username,
					avatar: userInfo.avatar,
					email: userInfo.email,
					accessToken: userInfo.accessToken,
					refreshToken: userInfo.refreshToken,
					tokenKey: userInfo.tokenKey,
					terminal: userInfo.terminal || 2
				};
				localStorage.setItem('userInfo', JSON.stringify(savedUserInfo));
				console.log('[Login] 保存用户信息到 localStorage:', savedUserInfo);
			} catch (error) {
				console.error('[Login] 保存用户信息到 localStorage 失败:', error);
			}
		},

		// 统一处理登录成功后的跳转
		handleLoginSuccess() {
			// 🔥 优先使用微信 OAuth 恢复的 state 中的 redirect（解决 OAuth 回调后 redirect 丢失问题）
			let redirect = this.$route.query.redirect;
			let agentId = this.$route.query.agentId;

			// 如果 URL 中没有 redirect，尝试从 wechatOAuthState 恢复
			if (!redirect && this.wechatOAuthState) {
				redirect = this.wechatOAuthState.redirect;
				agentId = agentId || this.wechatOAuthState.agentId;
				console.log('[WeChatOAuth] 从保存的 state 恢复 redirect:', redirect);
				// 清理已使用的 state
				sessionStorage.removeItem('wechat_oauth_state');
				this.wechatOAuthState = null;
			}

			console.log('[agent share debug] ========== Login: 登录成功，处理跳转 ==========');
			console.log('[agent share debug] [Login] redirect:', redirect);
			console.log('[agent share debug] [Login] agentId:', agentId);

			if (redirect) {
				// 有redirect参数，跳转回原页面
				console.log('[agent share debug] [Login] 📍 使用 redirect 参数跳转');

				// 🔧 正确处理 redirect 中的 query 参数
				// 例如：/agent/share/xxx?autoStart=true
				try {
					const url = new URL(redirect, window.location.origin);
					const path = url.pathname;
					const query = {};

					// 解析所有 query 参数
					url.searchParams.forEach((value, key) => {
						query[key] = value;
					});

					// 🔥 如果是智能体分享/详情页，自动添加 autoStart=true
					if ((path.startsWith('/agent/share/') || path.startsWith('/agent/profile/')) && !query.autoStart) {
						query.autoStart = 'true';
						console.log('[agent share debug] [Login] ✅ 检测到智能体页面，自动添加 autoStart=true');
					}

					console.log('[agent share debug] [Login] 解析后的路径:', path);
					console.log('[agent share debug] [Login] 解析后的query:', query);

					this.$router.push({ path, query });
					console.log('[agent share debug] [Login] ✅ 路由跳转已触发');
				} catch (e) {
					// 如果解析失败，回退到原来的逻辑
					console.warn('[agent share debug] [Login] ⚠️ URL解析失败，使用原始redirect:', e);
					this.$router.push(redirect);
				}
			} else if (agentId) {
				// 有agentId参数，直接进入智能体会话
				console.log('[agent share debug] [Login] 📍 使用 agentId 参数跳转');
				console.log('[agent share debug] [Login] agentId:', agentId);
				this.$router.push(`/home/chat?agentId=${agentId}`);
				console.log('[agent share debug] [Login] ✅ 路由跳转已触发');
			} else {
				// 默认跳转到首页
				console.log('[agent share debug] [Login] 📍 默认跳转到首页');
				this.$router.push("/home/index");
			}
			console.log('[agent share debug] ========== Login: 跳转处理完成 ==========');
		},

		// 统一登录处理
		submitLogin(formData) {
			this.accountLoginLoading = true;
			this.$http({
				url: "/login",
				method: 'post',
				data: formData
			})
				.then(async (data) => {
					// 保存密码到cookie(不安全)
					this.setCookie('username', formData.userName);
					this.setCookie('password', formData.password);
					// 保存token
					sessionStorage.setItem("accessToken", data.accessToken);
					sessionStorage.setItem("refreshToken", data.refreshToken);
					sessionStorage.setItem("tokenKey", data.tokenKey);

					// 🔥 智能体分享场景 - 保存基础用户信息到 localStorage
					this.saveUserInfoToLocalStorage({
						id: data.userId || data.id,
						userId: data.userId || data.id,
						username: formData.userName,
						accessToken: data.accessToken,
						refreshToken: data.refreshToken,
						tokenKey: data.tokenKey
					});

					if (window.BoxIMComponents) {
						this.$http({
							url: '/user/self',
							method: 'GET'
						}).then((userInfo) => {

							userInfo.userId = userInfo.id + "";
							userInfo.username = userInfo.nickName;
							// 兼容web模式和vscode集成模式，在vscode环境下才调用vscode服务
							// added by qinke. 从vue侧向vscode同步用户信息的唯一入口点，需要补充用户的accessToken用户vue侧的sessionStorage恢复
							// 集成原则：最小化的改动原来系统
							userInfo.tokenKey = data.tokenKey;
							userInfo.accessToken = data.accessToken;
							userInfo.apiKey = data.tokenKey;
							userInfo.terminal = 2;

							// 保存用户信息到 store - 修复切换用户时 ChatBox 中 mine.id 不更新的问题
							this.$store.commit('setUserInfo', userInfo);
							console.log("[坑爹位置]:", userInfo);
							window.vscodeServices?.get('IUserStateService').login(userInfo, data.accessToken);

							this.$store.commit('setIsLoggedIn', true)

							// 🔥 智能体分享场景 - 更新完整用户信息到 localStorage
							this.saveUserInfoToLocalStorage(userInfo);
						}).finally(() => {
							this.accountLoginLoading = false;
						})
					} else {
						console.log('[Login] Not in VSCode environment or services not available, using router');
						// Web环境 - 先加载用户信息，再跳转
						this.$http({
							url: '/user/self',
							method: 'GET'
						}).then((userInfo) => {
							userInfo.userId = userInfo.id + "";
							userInfo.username = userInfo.nickName;
							userInfo.tokenKey = data.tokenKey;
							userInfo.accessToken = data.accessToken;
							userInfo.apiKey = data.tokenKey;
							userInfo.terminal = 2;

							// 保存用户信息到 store
							this.$store.commit('setUserInfo', userInfo);
							this.$store.commit('setIsLoggedIn', true);

							// 更新完整用户信息到 localStorage
							this.saveUserInfoToLocalStorage(userInfo);

							// 跳转
							this.handleLoginSuccess();
						}).catch((error) => {
							console.error('[Login] 加载用户信息失败:', error);
							// 即使失败也跳转
							this.handleLoginSuccess();
						}).finally(() => {
							this.accountLoginLoading = false;
						});
					}
				})
				.catch(() => {
					this.accountLoginLoading = false;
				});
		},
		submitLogin2(formData) {
			this.smsLoginLoading = true;
			this.$http({
				url: "/loginByPhone",
				method: 'post',
				data: formData
			})
				.then(async (data) => {
					console.log("短信登录成功:", data);
					// 保存密码到cookie(不安全)
					this.setCookie('username', formData.userName);
					// this.setCookie('password', formData.password);
					// 保存token
					sessionStorage.setItem("accessToken", data.accessToken);
					sessionStorage.setItem("refreshToken", data.refreshToken);
					sessionStorage.setItem("tokenKey", formData.tokenKey);

					// 🔥 智能体分享场景 - 保存基础用户信息到 localStorage
					this.saveUserInfoToLocalStorage({
						id: data.userId || data.id,
						userId: data.userId || data.id,
						username: formData.userName,
						accessToken: data.accessToken,
						refreshToken: data.refreshToken,
						tokenKey: formData.tokenKey
					});

					// vscode集成处理
					if (window.BoxIMComponents) {
						this.$http({
							url: '/user/self',
							method: 'GET'
						}).then((userInfo) => {

							userInfo.userId = userInfo.id + "";
							userInfo.username = userInfo.nickName;
							userInfo.accessToken = data.accessToken;
							userInfo.apiKey = formData.tokenKey; // 傻逼设计，在上一个接口中有token
							userInfo.terminal = 2;

							// 保存用户信息到 store - 修复切换用户时 ChatBox 中 mine.id 不更新的问题
							this.$store.commit('setUserInfo', userInfo);
							console.log("[坑爹位置]:", userInfo);
							window.vscodeServices?.get('IUserStateService').login(userInfo, data.accessToken);

							this.$store.commit('setIsLoggedIn', true)

							// 🔥 智能体分享场景 - 更新完整用户信息到 localStorage
							this.saveUserInfoToLocalStorage(userInfo);
						}).finally(() => {
							this.smsLoginLoading = false;
						})

					} else {
						console.log('[Login] Not in VSCode environment or services not available, using router');
						// Web环境 - 先加载用户信息，再跳转
						this.$http({
							url: '/user/self',
							method: 'GET'
						}).then((userInfo) => {
							userInfo.userId = userInfo.id + "";
							userInfo.username = userInfo.nickName;
							userInfo.accessToken = data.accessToken;
							userInfo.apiKey = formData.tokenKey;
							userInfo.terminal = 2;

							// 保存用户信息到 store
							this.$store.commit('setUserInfo', userInfo);
							this.$store.commit('setIsLoggedIn', true);

							// 更新完整用户信息到 localStorage
							this.saveUserInfoToLocalStorage(userInfo);

							// 跳转
							this.handleLoginSuccess();
						}).catch((error) => {
							console.error('[Login] 加载用户信息失败:', error);
							// 即使失败也跳转
							this.handleLoginSuccess();
						}).finally(() => {
							this.smsLoginLoading = false;
						});
					}
				})
				.catch(() => {
					this.smsLoginLoading = false;
				});
		},
		async phoneLogin() {
			// 验证手机号是否输入
			if (!this.smsForm.userName) {
				this.$message.error('请先输入手机号');
				return;
			}

			// 验证验证码是否输入
			if (!this.smsForm.password) {
				this.$message.error('请输入验证码');
				return;
			}

			// 验证手机号格式
			const phoneRegex = /^1[3-9]\d{9}$/;
			if (!phoneRegex.test(this.smsForm.userName)) {
				this.$message.error('请输入正确的手机号格式');
				return;
			}

			this.smsLoginLoading = true;

			try {
				// 步骤1：调用后端验证验证码（安全验证）
				console.log('[PhoneLogin] 开始后端验证码验证，手机号:', this.smsForm.userName);
				const verifyResponse = await axios({
					url: "https://shadan.web.service.thinkgs.cn/jeecg-boot/sys/verifySms",
					method: 'post',
					data: {
						mobile: this.smsForm.userName,
						code: this.smsForm.password
					},
					timeout: 10000
				});

				console.log('[PhoneLogin] 验证码验证响应:', verifyResponse.data);

				if (!verifyResponse.data.success) {
					this.$message.error(verifyResponse.data.message || "验证码验证失败");
					this.smsLoginLoading = false;
					return;
				}

				console.log('[PhoneLogin] 验证码验证成功，开始获取用户信息');

				// 步骤2：验证成功，获取用户信息
				const userInfo = await this.$http({
					url: "/phoneLoginInfo",
					method: 'post',
					data: {
						phone: this.smsForm.userName,
						// 重要：区分terminal和terminalNo
						terminal: this.currentTerminalType, // 根据实际环境设置
						terminalNo: 0  // 必须为0，默认账号类型
					},
					timeout: 10000
				});

				console.log("用户信息查询成功:", userInfo);
				this.accountForm.userName = userInfo.userName;
				this.accountForm.password = userInfo.password;
				this.accountForm.phone = userInfo.phone;
				this.accountForm.tokenKey = userInfo.tokenKey;
				this.submitLogin2(this.accountForm);
			} catch (error) {
				console.error("[PhoneLogin] 短信登录失败:", error);
				this.$message.error(error.message || "短信登录失败");
				this.smsLoginLoading = false;
			}
		},

		// 兼容方法：根据当前活动tab调用相应的提交方法
		submitForm(formName) {
			if (this.activeTab === 'account') {
				this.submitAccountForm();
			} else if (this.activeTab === 'sms') {
				this.submitSmsForm();
			}
		},

		// 开始二维码过期定时器
		startQrcodeTimer() {
			if (this.qrcodeTimer) {
				clearTimeout(this.qrcodeTimer);
			}
			// 2分钟后过期
			this.qrcodeTimer = setTimeout(() => {
				if (this.qrcodeStatus === 'active') {
					this.qrcodeStatus = 'expired';
					console.log('二维码已过期，3秒后自动刷新');
					// 3秒后自动刷新二维码
					setTimeout(() => {
						this.refreshQrcode();
					}, 3000);
				}
			}, 120000);
		},

		// 刷新二维码
		refreshQrcode() {
			console.log('开始刷新二维码');
			// 重新生成UUID
			this.UUID = uuidv4();
			// 重置二维码状态为加载中
			this.qrcodeStatus = 'loading';
			// 清空旧的ticket
			this.wxTicket = '';
			// 重置ticket相关状态
			this.wxTicketReady = false;
			this.wxTicketRetryCount = 0;

			// 停止旧的定时器
			this.stopWxTicketTimer();
			this.stopCheckLoginTimer();

			// 延迟500ms后开始新的流程，给用户一个加载的感觉
			setTimeout(() => {
				// 重新设置状态为active
				this.qrcodeStatus = 'active';
				// 重新启动ticket定时器（CheckLogin定时器由 GetWxTicket 成功后启动）
				this.startWxTicketTimer();
				this.startQrcodeTimer();
				console.log('二维码刷新完成');
			}, 500);
		},

		resetForm(formName) {
			if (this.$refs[formName]) {
				this.$refs[formName].resetFields();
			}
		},

		getCookie(name) {
			let reg = new RegExp("(^| )" + name + "=([^;]*)(;|$)");
			let arr = document.cookie.match(reg)
			if (arr) {
				return unescape(arr[2]);
			}
			return '';
		},

		setCookie(name, value) {
			document.cookie = name + "=" + escape(value);
		},

		// 启动CheckLogin定时器
		startCheckLoginTimer() {
			// 🔥 防御性检查：只在扫码登录tab时才启动定时器
			if (this.activeTab !== 'qrcode') {
				console.log('[Login] 当前不在扫码登录tab，拒绝启动CheckLogin定时器');
				return;
			}

			console.log('[Login] 启动CheckLogin定时器');
			// 先清除可能存在的定时器
			this.stopCheckLoginTimer();

			// 立即调用一次
			this.CheckLogin();

			// 设置每1秒调用一次
			this.checkLoginTimer = setInterval(() => {
				// 每次轮询前检查是否还在扫码登录tab
				if (this.activeTab === 'qrcode') {
					this.CheckLogin();
				} else {
					console.log('[Login] 检测到已切换到非扫码登录tab，停止CheckLogin轮询');
					this.stopCheckLoginTimer();
				}
			}, 1000); // 1000毫秒 = 1秒
		},

		// 停止CheckLogin定时器
		stopCheckLoginTimer() {
			if (this.checkLoginTimer) {
				console.log('停止CheckLogin定时器');
				clearInterval(this.checkLoginTimer);
				this.checkLoginTimer = null;
			}
		},

		// 启动微信ticket定时器（CheckLogin定时器由 GetWxTicket 成功后启动）
		startWxTicketTimer() {
			// 🔥 防御性检查：只在扫码登录tab时才启动定时器
			if (this.activeTab !== 'qrcode') {
				console.log('[Login] 当前不在扫码登录tab，拒绝启动微信ticket定时器');
				return;
			}

			console.log('[Login] 启动微信ticket定时器');
			// 先清除可能存在的定时器
			this.stopWxTicketTimer();

			// 如果 ticket 还没获取到才立即调用，否则跳过（created 里已提前请求）
			if (!this.wxTicketReady) {
				this.GetWxTicket();
			} else {
				// ticket 已就绪，直接启动 CheckLogin 轮询
				this.startCheckLoginTimer();
			}

			// 设置每1分钟刷新一次 ticket
			this.wxTicketTimer = setInterval(() => {
				if (this.activeTab === 'qrcode') {
					this.GetWxTicket();
				} else {
					console.log('[Login] 检测到已切换到非扫码登录tab，停止微信ticket轮询');
					this.stopWxTicketTimer();
				}
			}, 60000);
		},

		// 停止微信ticket定时器
		stopWxTicketTimer() {
			if (this.wxTicketTimer) {
				console.log('停止微信ticket定时器');
				clearInterval(this.wxTicketTimer);
				this.wxTicketTimer = null;
			}
		},
		sendBindPhoneCode() {
			// 验证手机号是否输入
			if (!this.bindPhoneForm.phone) {
				this.$message.error('请先输入手机号');
				return;
			}

			// 验证手机号格式
			const phoneRegex = /^1[3-9]\d{9}$/;
			if (!phoneRegex.test(this.bindPhoneForm.phone)) {
				this.$message.error('请输入正确的手机号格式');
				return;
			}

			// 调用后端发送验证码API
			console.log('发送绑定手机号验证码到:', this.bindPhoneForm.phone);

			// 使用原生axios调用外部接口，绕过项目的响应拦截器
			axios({
				url: "https://shadan.web.service.thinkgs.cn/jeecg-boot/sys/sendsms",
				method: 'post',
				data: { mobile: this.bindPhoneForm.phone },
				timeout: 10000
			}).then(response => {
				console.log('绑定手机号验证码发送响应:', response.data);

				// 适配外部接口的返回格式：success字段表示成功
				if (response.data && response.data.success === true) {
					this.$message.success('验证码已发送');
					// 注意：验证码不再存储在前端，由后端 /verifySms 接口验证
					this.startBindPhoneCountdown();
				} else {
					console.error('服务器返回错误:', response.data);
					this.$message.error(response.data.message || '验证码发送失败');
				}
			}).catch(error => {
				console.error('绑定手机号验证码发送失败:', error);

				// 处理不同类型的错误
				let errorMessage = '验证码发送失败，请稍后重试';
				if (error.response && error.response.data) {
					// 如果是HTTP错误响应
					const data = error.response.data;
					if (data.success === false) {
						errorMessage = data.message || errorMessage;
					}
				} else if (error.message) {
					errorMessage = error.message;
				}

				this.$message.error(errorMessage);
			});
		},

		// 开始绑定手机号验证码倒计时
		startBindPhoneCountdown() {
			this.bindPhoneCountdown = 60;
			this.bindPhoneTimer = setInterval(() => {
				this.bindPhoneCountdown--;
				if (this.bindPhoneCountdown <= 0) {
					clearInterval(this.bindPhoneTimer);
					this.bindPhoneTimer = null;
				}
			}, 1000);
		},

		submitBindPhone() {
			// 兼容移动端和Web端的ref
			const formRef = this.$refs.bindPhoneForm || this.$refs.bindPhoneFormWeb;
			if (!formRef) {
				this.$message.error("表单引用不存在，请刷新页面重试");
				return;
			}

			formRef.validate(async (valid) => {
				if (valid) {
					this.bindPhoneLoading = true;

					try {
						// 步骤1：调用后端验证验证码（安全验证）
						console.log('[BindPhone] 开始后端验证码验证，手机号:', this.bindPhoneForm.phone);
						const verifyResponse = await axios({
							url: "https://shadan.web.service.thinkgs.cn/jeecg-boot/sys/verifySms",
							method: 'post',
							data: {
								mobile: this.bindPhoneForm.phone,
								code: this.bindPhoneForm.code
							},
							timeout: 10000
						});

						console.log('[BindPhone] 验证码验证响应:', verifyResponse.data);

						if (!verifyResponse.data.success) {
							this.$message.error(verifyResponse.data.message || "验证码验证失败");
							this.bindPhoneLoading = false;
							return;
						}

						console.log('[BindPhone] 验证码验证成功，开始绑定手机号和openid:', this.currentOpenid);

						// 步骤2：验证成功，继续绑定流程
						const userInfo = await this.$http({
							url: "/phoneLoginInfo",
							method: 'post',
							data: {
								phone: this.bindPhoneForm.phone,
								// 重要：区分terminal和terminalNo
								terminal: this.currentTerminalType, // 根据实际环境设置
								terminalNo: 0  // 必须为0，默认账号类型
							},
							timeout: 10000
						});

						console.log("[BindPhone] 用户信息查询成功:", userInfo);
						this.accountForm.userName = userInfo.userName;
						this.accountForm.password = userInfo.password;
						this.accountForm.phone = userInfo.phone;
						this.accountForm.tokenKey = userInfo.tokenKey;

						// 步骤3：更新用户 openid 绑定
						await this.$http({
							url: "/user/update2",
							method: "put",
							data: {
								id: userInfo.id,
								userName: userInfo.userName,
								phone: this.bindPhoneForm.phone,
								openid: this.currentOpenid,
								nickName: userInfo.nickName
							}
						});

						// 步骤4：登录
						this.bindPhoneLoading = false;
						this.submitLogin2(this.accountForm);

						// 重置绑定状态
						this.bindPhone = false;
						this.bindPhoneForm.phone = '';
						this.bindPhoneForm.code = '';

					} catch (error) {
						console.error("[BindPhone] 绑定流程失败:", error);
						this.$message.error(error.message || "绑定失败");
						this.bindPhoneLoading = false;
					}
				}
			});
		},

		cancelBindPhone() {
			// 重置绑定状态
			this.bindPhone = false;
			this.bindPhoneForm.phone = '';
			this.bindPhoneForm.code = '';
			this.currentOpenid = '';
			this.isNewUser = false;

			// 清除倒计时
			if (this.bindPhoneTimer) {
				clearInterval(this.bindPhoneTimer);
				this.bindPhoneTimer = null;
				this.bindPhoneCountdown = 0;
			}

			// 🔥 关键修复：只在当前为扫码登录tab时才启动定时器
			if (this.activeTab === 'qrcode') {
				console.log('[Login] 取消绑定，当前在扫码登录tab，重新启动定时器');
				// 重新启动扫码登录（checkLoginTimer 由 GetWxTicket 成功后启动）
				this.startWxTicketTimer();
			} else {
				console.log('[Login] 取消绑定，当前不在扫码登录tab，不启动定时器');
			}

			this.$message.info('已取消绑定');
		},

		// 在VSCode环境中通过浏览器打开注册页面
		openRegisterInBrowser() {
			console.log('[Register] Opening register page in browser from VSCode environment');
			
			// 使用固定的注册页面URL
			const registerUrl = 'https://aiim.service.thinkgs.cn/#/register';
			
			console.log('[Register] Register URL:', registerUrl);
			
			// 尝试使用VSCode服务打开外部链接
			if (window.vscodeServices && window.vscodeServices.openExternal) {
				window.vscodeServices.openExternal(registerUrl);
				console.log('[Register] Opened register page using vscodeServices.openExternal');
			} else if (window.vscodeServices && window.vscodeServices.showInfo) {
				// 如果没有openExternal方法，提示用户手动打开
				window.vscodeServices.showInfo(`请在浏览器中打开: ${registerUrl}`);
				console.log('[Register] Showed info message with register URL');
			} else {
				// 降级方案：使用window.open
				window.open(registerUrl, '_blank');
				console.log('[Register] Opened register page using window.open');
			}
		}
	},

	created() {
		// 提前发起二维码请求，不等待 DOM 渲染
		this.GetWxTicket();
	},

	async mounted() {
		// 优先级1: 微信浏览器中的 OAuth 处理
		if (this.isInWeChatBrowser()) {
			console.log('[WeChatOAuth] 检测到微信浏览器环境');

			// 检查是否为 OAuth 回调（URL 中有 code 参数）
			const isCallback = await this.handleWeChatOAuthCallback();
			if (isCallback) {
				console.log('[WeChatOAuth] OAuth 回调处理中...');
				return;  // OAuth 流程处理中，不继续执行其他逻辑
			}

			// 非回调，检查是否需要发起授权
			if (!sessionStorage.getItem('wechat_oauth_attempted')) {
				console.log('[WeChatOAuth] 首次访问，发起微信授权');
				sessionStorage.setItem('wechat_oauth_attempted', 'true');
				this.initiateWeChatOAuth();
				return;
			}

			// 已尝试过授权但失败或用户取消，显示正常登录界面
			console.log('[WeChatOAuth] 已尝试过授权，显示正常登录界面');
		}

		// 优先级2: 检查是否需要自动登录（URL 中有 phone 参数）
		this.handleAutoLogin();

		// 根据环境设置正确的终端类型
		// 重要：terminal根据环境动态设置，terminalNo始终为0
		this.accountForm.terminal = this.currentTerminalType;
		this.accountForm.terminalNo = 0; // 必须为0，默认账号类型
		this.smsForm.terminal = this.currentTerminalType;
		this.smsForm.terminalNo = 0; // 必须为0，默认账号类型

		// 初始化账号登录表单数据
		this.accountForm.userName = this.getCookie("username");
		this.accountForm.password = this.getCookie("password");

		// 调试VSCode环境检测
		console.log('[Login] Component mounted with props:', {
			isVSCodeEnvironment: this.isVSCodeEnvironment,
			vscodeServices: this.vscodeServices,
			hasVscodeServices: !!this.vscodeServices
		});

		// 调试Store状态
		console.log('[Login] Store状态检查:', {
			hasStore: !!this.$store,
			hasState: !!this.$store?.state,
			hasUserStore: !!this.$store?.state?.userStore,
			currentIsLoggedIn: this.$store?.state?.userStore?.isLoggedIn,
			hasSetIsLoggedInAction: !!this.$store?._actions?.setIsLoggedIn,
			storeModules: this.$store?.state ? Object.keys(this.$store.state) : 'no state'
		});

		// 初始化二维码和微信ticket定时器
		if (this.activeTab === 'qrcode') {
			this.startQrcodeTimer();
			this.startWxTicketTimer();
			// 注意：不直接启动 checkLoginTimer，由 GetWxTicket 成功后启动
		}
	},

	watch: {
		// 监听tab切换
		activeTab(newTab, oldTab) {
			console.log('[Login] Tab switching:', oldTab, '->', newTab);

			// 在VSCode环境下，确保表单引用正确更新
			if (this.isInVSCodeEnvironment()) {
				this.$nextTick(() => {
					// 重置对应的表单
					if (newTab === 'account' && this.$refs.accountForm) {
						console.log('[Login] Resetting account form for VSCode');
						// 不重置字段，保留用户输入
					} else if (newTab === 'sms' && this.$refs.smsForm) {
						console.log('[Login] Resetting SMS form for VSCode');
						// 不重置字段，保留用户输入
					}
				});
			}

			// 🔥 关键修复：先停止所有定时器，避免定时器泄漏
			console.log('[Login] 停止所有二维码相关定时器');
			this.stopWxTicketTimer();
			this.stopCheckLoginTimer();
			this.wxTicketReady = false;

			if (newTab === 'qrcode') {
				// 切换到扫码登录，重新启动定时器
				console.log('[Login] 切换到扫码登录tab，启动定时器');
				if (this.qrcodeStatus === 'active') {
					this.startQrcodeTimer();
				}
				// 启动微信ticket定时器（CheckLogin定时器由 GetWxTicket 成功后启动）
				this.startWxTicketTimer();
			} else {
				// 切换到其他标签，定时器已在上面停止
				console.log('[Login] 切换到非扫码登录tab，定时器已停止');
			}
		}
	},

	// 组件销毁时清理定时器
	beforeDestroy() {
		if (this.timer) {
			clearInterval(this.timer);
		}
		if (this.qrcodeTimer) {
			clearTimeout(this.qrcodeTimer);
		}
		// 清理微信ticket定时器
		this.stopWxTicketTimer();
		// 清理CheckLogin定时器
		this.stopCheckLoginTimer();
		// 清理绑定手机号定时器
		if (this.bindPhoneTimer) {
			clearInterval(this.bindPhoneTimer);
			this.bindPhoneTimer = null;
		}
	}
}
</script>

<style scoped lang="scss">
.login-view {
	width: 100%;
	height: 100%;
	background: #E8F2FF;
	background-size: cover;
	box-sizing: border-box;

	// 自动登录加载状态样式
	.auto-login-loading {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: #E8F2FF;
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 9999;

		.loading-content {
			background: white;
			padding: 40px;
			border-radius: 10px;
			box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
			text-align: center;
			min-width: 300px;

			.loading-spinner {
				margin-bottom: 20px;

				i {
					font-size: 48px;
					color: #409EFF;
					animation: rotating 2s linear infinite;
				}
			}

			.loading-text {
				font-size: 18px;
				font-weight: 600;
				color: #333;
				margin-bottom: 10px;
			}

			.loading-phone {
				font-size: 14px;
				color: #666;
			}
		}
	}

	// 旋转动画
	@keyframes rotating {
		from {
			transform: rotate(0deg);
		}

		to {
			transform: rotate(360deg);
		}
	}

	.login-content {
		position: relative;
		display: flex;
		justify-content: space-around;
		align-items: center;
		padding: 10%;

		.login-form {
			width: 450px;
			padding: 30px;
			background: white;
			opacity: 0.95;
			box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
			border-radius: 10px;
			overflow: hidden;

			.login-brand {
				display: flex;
				justify-content: center;
				align-items: center;
				line-height: 50px;
				margin: 20px 0 30px 0;
				font-size: 22px;
				font-weight: 600;
				letter-spacing: 2px;
				text-transform: uppercase;
				text-align: center;

				.logo {
					width: 30px;
					height: 30px;
					margin-right: 10px;
				}
			}

			// 自定义Tab样式 - VSCode环境专用
			.custom-tabs {
				.custom-tabs-header {
					display: flex;
					border-bottom: 2px solid #DCDFE6;
					margin-bottom: 20px;
					
					.custom-tab-item {
						flex: 1;
						text-align: center;
						padding: 12px 0;
						font-size: 16px;
						font-weight: 500;
						color: #303133;
						cursor: pointer;
						position: relative;
						transition: all 0.3s;
						user-select: none;
						
						&:hover {
							color: #409EFF;
						}
						
						&.active {
							color: #409EFF;
							
							&::after {
								content: '';
								position: absolute;
								bottom: -2px;
								left: 0;
								right: 0;
								height: 2px;
								background-color: #409EFF;
							}
						}
					}
				}
				
				.custom-tabs-content {
					.custom-tab-pane {
						animation: fadeIn 0.3s;
					}
				}
			}
			
			// 原有的el-tabs样式
			.login-tabs {
				// 防止被全局样式改成 column-reverse，导致 tab 跑到内容底部
				flex-direction: column !important;

				.el-tabs__header {
					margin: 0 0 20px 0;
				}

				.el-tabs__nav-wrap::after {
					height: 1px;
				}

				.el-tabs__item {
					font-size: 16px;
					font-weight: 500;
				}

				.el-tabs__content {
					padding-top: 10px;
				}
			}

			// 验证码行样式
			.verification-code-row {
				display: flex;
				align-items: center;
				gap: 10px;

				.code-input {
					flex: 1;
				}

				.send-code-btn {
					flex-shrink: 0;
					min-width: 100px;
					font-size: 12px;
				}
			}

			// 登录按钮样式
			.login-btn {
				width: 100%;
				height: 40px;
				font-size: 16px;
				font-weight: 500;
			}

			// 二维码容器样式
			.qrcode-container {
				display: flex;
				justify-content: center;
				align-items: center;
				min-height: 300px;

				.qrcode-box {
					text-align: center;

					.qrcode-loading,
					.qrcode-expired,
					.qrcode-scanned {
						display: flex;
						flex-direction: column;
						align-items: center;
						gap: 15px;

						i {
							font-size: 48px;
							color: #409EFF;
						}

						p {
							margin: 0;
							font-size: 14px;
							color: #666;
						}
					}

					.qrcode-active {
						.qrcode-image {
							margin-bottom: 15px;

							.mock-qrcode {
								width: 200px;
								height: 200px;
								border: 2px dashed #ddd;
								display: flex;
								flex-direction: column;
								justify-content: center;
								align-items: center;
								background: #f9f9f9;

								i {
									font-size: 48px;
									color: #999;
									margin-bottom: 10px;
								}

								p {
									margin: 0;
									color: #999;
									font-size: 14px;
								}
							}

							.qrcode-real {
								width: 200px;
								height: 200px;
								display: flex;
								justify-content: center;
								align-items: center;
								border: 1px solid #ddd;
								border-radius: 8px;
								overflow: hidden;

								img {
									width: 100%;
									height: 100%;
									object-fit: contain;
								}
							}
						}

						.qrcode-tip {
							margin: 0 0 10px 0;
							font-size: 14px;
							color: #666;
						}
					}

					.qrcode-expired {
						i {
							color: #F56C6C;
						}

						.auto-refresh-tip {
							margin: 10px 0;
							font-size: 12px;
							color: #909399;
							animation: blink 1.5s infinite;
						}

						.manual-refresh-btn {
							margin-top: 15px;
							color: #409EFF;
							font-size: 14px;
							padding: 8px 16px;

							&:hover {
								color: #66B1FF;
								background-color: rgba(64, 158, 255, 0.1);
							}

							i {
								color: inherit;
								margin-right: 5px;
								font-size: 14px;
							}
						}
					}

					.qrcode-scanned {
						i {
							color: #67C23A;
						}
					}
				}
			}

			// 绑定手机号容器样式
			.bind-phone-container {
				min-height: 300px;
				display: flex;
				flex-direction: column;
				justify-content: center;

				.bind-phone-header {
					text-align: center;
					margin-bottom: 30px;

					h3 {
						margin: 0 0 10px 0;
						font-size: 20px;
						font-weight: 600;
						color: #333;
					}

					p {
						margin: 0;
						font-size: 14px;
						color: #666;
					}
				}

				.cancel-btn {
					width: 100%;
					margin-top: 10px;
					color: #999;

					&:hover {
						color: #409EFF;
					}
				}
			}

			.register {
				display: flex;
				flex-direction: row-reverse;
				line-height: 40px;
				text-align: left;
				padding-left: 20px;
				margin-top: 20px;
			}
		}
	}
}

// 响应式设计
@media (max-width: 768px) {
	.login-view .login-content .login-form {
		width: 90%;
		padding: 20px;
	}

	// 自动登录加载状态的响应式设计
	.login-view .auto-login-loading .loading-content {
		width: 90%;
		padding: 30px 20px;
		min-width: auto;
	}
}

// 闪烁动画
@keyframes blink {
	0%,
	50% {
		opacity: 1;
	}

	51%,
	100% {
		opacity: 0.3;
	}
}

// 淡入动画
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
